import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import { Loader2, AlertCircle } from 'lucide-react'

export default function AcceptInvitePage() {
    const { token } = useParams<{ token: string }>()
    const navigate = useNavigate()
    const { setTokens, fetchUser } = useAuthStore()

    const [name, setName] = useState('')
    const [password, setPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        setError(null)

        try {
            const response = await api.post<any>('/auth/accept-invitation', {
                token,
                name,
                password,
            })

            const { accessToken, refreshToken, expiresAt } = response.data
            setTokens(accessToken, refreshToken, expiresAt)
            await fetchUser()

            toast({ title: 'Welcome!', description: 'You have joined the team.' })
            navigate('/', { replace: true })
        } catch (err: any) {
            setError(err.message || 'Failed to accept invitation')
        } finally {
            setIsLoading(false)
        }
    }

    if (error) {
        return (
            <div className="text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                    <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
                <h1 className="text-xl font-bold mb-2">Invitation Error</h1>
                <p className="text-muted-foreground mb-6">{error}</p>
                <Button onClick={() => navigate('/login')}>
                    Go to Login
                </Button>
            </div>
        )
    }

    return (
        <div className="animate-in">
            <div className="mb-8">
                <h1 className="text-2xl font-bold mb-2">Join the team</h1>
                <p className="text-muted-foreground">
                    Complete your account to accept the invitation
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="name">Your name</Label>
                    <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        autoComplete="name"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="password">Create a password</Label>
                    <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        minLength={8}
                    />
                    <p className="text-xs text-muted-foreground">
                        At least 8 characters with uppercase, lowercase, and number
                    </p>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Accept Invitation
                </Button>
            </form>
        </div>
    )
}
